import type React from "react";
import { useMemo, useRef } from "react";
import { noop } from "lodash";
import { DropdownContainer } from "./DropdownContainer";
import { SimpleDropdownItem, DropdownButton } from "@similarweb/ui-components";

import I18n from "pro-features/components/WithTranslation/src/I18n";
import { allTrackers } from "app/services/track/track";
import { i18nFilter } from "app/filters/i18nFilter";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import {
    SEGMENTS_FAMILY_NAME,
    SEGMENTS_GROUP_METRICS,
    SEGMENTS_GROUP_TOTAL_METRICS,
    SEGMENTS_TOTAL_METRICS,
} from "app/components/widget/widget-utilities/segments/constants";
import type { MetricItems, WidgetMetrics } from "./types";
import { swSettings } from "app/common/services/swSettings";
import { DESKTOP_WEB_SOURCE } from "./segments/constants";

interface DashboardWizardMetricsProps {
    changeMetric: (metricId: string) => void;
    family: string;
    selectedMetric: string;
    isSegmentsGroup?: boolean;
    selectedCountry?: number;
    webSource?: string;
    wrapperClassName?: string;
    onDropDownToggle?: (isOpen: boolean) => void;
    shouldTrackOnSelect?: boolean;
}

const DashboardWizardMetrics: React.FC<DashboardWizardMetricsProps> = ({
    changeMetric,
    family,
    selectedMetric,
    isSegmentsGroup,
    selectedCountry,
    webSource,
    wrapperClassName,
    onDropDownToggle = noop,
    shouldTrackOnSelect = true,
}) => {
    const selectedMetricId = useRef(0);

    const metricItems: MetricItems[] = useMemo(() => {
        let result: MetricItems[] = widgetSettings
            .getWidgetMetrics()
            .filter((metric: WidgetMetrics) => metric.family === family && !metric.isDisabled)
            .map((metric: WidgetMetrics, index) => {
                if (selectedMetric === metric.id) {
                    selectedMetricId.current = index;
                }

                const metricText = i18nFilter()(`dashboard.metricGallery.${metric.family}.${metric.id}.title`);

                return {
                    text: metricText,
                    metricId: metric.id,
                    id: index,
                    key: index,
                    order: metric.order,
                    dropdownOrder: metric.dropdownOrder,
                };
            })
            .sort((firstMetric, secondMetric) => firstMetric.dropdownOrder - secondMetric.dropdownOrder);

        if (family === SEGMENTS_FAMILY_NAME) {
            const allowedTotalCountries = swSettings?.components?.TotalWebCustomSegmentsAnalysis?.resources?.Countries;

            if (allowedTotalCountries.includes(Number(selectedCountry)) && webSource !== DESKTOP_WEB_SOURCE) {
                result = result.filter((metric: MetricItems) =>
                    isSegmentsGroup
                        ? SEGMENTS_GROUP_TOTAL_METRICS[metric.metricId]
                        : SEGMENTS_TOTAL_METRICS[metric.metricId],
                );
            } else {
                result = result.filter((metric: MetricItems) =>
                    isSegmentsGroup
                        ? SEGMENTS_GROUP_METRICS[metric.metricId]
                        : !SEGMENTS_GROUP_METRICS[metric.metricId],
                );
            }
        }

        return result;
    }, [selectedCountry, webSource]);

    const contents = [
        <DropdownButton key="metric-dropdown-header">
            <I18n>metricItems[selectedMetricId].text</I18n>
        </DropdownButton>,
        ...metricItems,
    ];
    const onSelect = (item) => {
        if (shouldTrackOnSelect) {
            allTrackers.trackEvent("Drop Down", "click", `Metric/${item.text}`);
        }
        changeMetric(item.metricId);
    };

    return (
        <div className={wrapperClassName}>
            <DropdownContainer
                initialSelection={{ [selectedMetricId.current]: true }}
                hasSearch={true}
                itemsComponent={SimpleDropdownItem}
                onSelect={onSelect}
                dropdownPopupHeight={150}
                searchPlaceHolder={`Search metric`}
                onToggle={onDropDownToggle}
                noResultsMessage={i18nFilter()("dropdown.no.results")}
            >
                {contents}
            </DropdownContainer>
        </div>
    );
};

export default DashboardWizardMetrics;
